/**
 * Per-slug per-locale deep-copy translations for case study pages.
 * EN remains the canonical source baked into /case/[slug]/page.tsx;
 * SQ + AR translations live here keyed by slug. Missing translations
 * fall back to EN automatically.
 *
 * ── Keep in lockstep with the EN canonical ──────────────────────────────────
 * These are TRANSLATIONS of projectDeepCopy in /case/[slug]/page.tsx, not an
 * independent narrative. The 2026-08 audit found this file still serving the
 * pre-audit copy (Creed VR "archived", Termka as a retail POS, Alemiin with
 * "merchants live") a full cycle after the EN was corrected — SQ/AR readers
 * got the lies the EN had already dropped. When the EN deep copy changes,
 * change the matching entry here in the same commit, or delete the entry and
 * let the page fall back to EN until a real translation lands.
 */

import type { Locale } from "@/lib/i18n";

export type CaseDeepCopy = {
  lede: string;
  problem: string;
  approach: string;
  outcome: string;
};

type Translations = Record<string, Partial<Record<Locale, CaseDeepCopy>>>;

export const CASE_TRANSLATIONS: Translations = {
  alemiin: {
    sq: {
      lede:
        "Një treg për gjithçka që shqiptarët tregtojnë vërtet — produkte, shërbime, prona dhe automjete — pas një llogarie të vetme, me zero komision për të dyja anët.",
      problem:
        "Shitblerja shqiptare ndodh në grupe të fragmentuara Facebook-u, pa verifikim dhe pa shtresë besimi. Platformat që ekzistojnë marrin 15–30% të çdo shitjeje — çmim që i nxjerr jashtë loje shitësit e vegjël, të cilët përbëjnë pjesën më të madhe të tregut.",
      approach:
        "Një sipërfaqe e vetme multi-treg me katër modele kategorish nën një llogari, panel shitësi me inventar dhe gjurmim porosish, shenja shitësi të verifikuar me vlerësime dhe zgjidhje mosmarrëveshjesh, dhe një model çmimesh me abonim në vend të komisionit.",
      outcome:
        "Në prag lansimi. Lista e Themeluesve është e hapur para hapjes — data e hapjes dhe afati i vendeve të Themeluesve jetojnë në numëratorin e vetë alemiin.com, i cili është burimi i së vërtetës ku kjo faqe referohet.",
    },
    ar: {
      lede:
        "سوق واحد لكل ما يتداوله الألبان فعلاً — منتجات وخدمات وعقارات ومركبات — خلف حساب واحد، وبعمولة صفرية على الطرفين.",
      problem:
        "البيع والشراء الألباني يجري في مجموعات فيسبوك مبعثرة، بلا توثيق وبلا طبقة ثقة. أمّا المنصات القائمة فتقتطع 15–30% من كل عملية بيع — كلفة تُقصي صغار البائعين الذين يشكّلون معظم السوق.",
      approach:
        "سطح واحد متعدد الأسواق بأربعة نماذج فئات تتشارك حساباً واحداً، ولوحة بائع بالمخزون وتتبّع الطلبات، وشارات بائع موثّق مع تقييمات وحلّ نزاعات، ونموذج تسعير بالاشتراك بدل العمولة.",
      outcome:
        "قيد الإطلاق. قائمة المؤسِّسين مفتوحة قبل الافتتاح — تاريخ الافتتاح وموعد إغلاق مقاعد المؤسِّسين يعيشان على عدّاد alemiin.com نفسه، وهو مصدر الحقيقة الذي تحتكم إليه هذه الصفحة.",
    },
  },
  planzero: {
    sq: {
      lede:
        "Qendra e operacioneve mbi të cilën punon një studio njëvetëshe: projekte, QA, përmbajtje dhe të ardhura klientësh në një hapësirë të vetme, në vend të katër veglave të shkëputura.",
      problem:
        "Të drejtosh një studio vetëm do të thotë që puna jeton në një gjurmues projektesh, defektet në një vegël të dytë, kalendari i përmbajtjes në një të tretë dhe paratë në një spreadsheet. Asgjë nuk pajtohet me tjetrën, kështu që themeluesi bëhet vetë shtresa e integrimit.",
      approach:
        "Një hapësirë e vetme e autentikuar ku projekti mbart bashkë radhën e vet të QA-së, tubacionin e përmbajtjes dhe linjën e të ardhurave — statusi bëhet një kërkesë e vetme, jo katër ndërrime tabesh.",
      outcome:
        "Live dhe në përdorim të përditshëm — kjo studio planifikon, gjurmon dhe faturon punën e vet përmes tij, përfshirë sajtin që po lexoni.",
    },
    ar: {
      lede:
        "مركز العمليات الذي يُدار به استوديو من شخص واحد: المشاريع وضبط الجودة والمحتوى وإيرادات العملاء في مساحة عمل واحدة بدل أربع أدوات متفرّقة.",
      problem:
        "إدارة استوديو منفرداً تعني أن العمل يعيش في متتبّع مشاريع، والأعطال في أداة ثانية، وتقويم المحتوى في ثالثة، والمال في جدول بيانات. لا شيء يتطابق مع الآخر، فيصير المؤسِّس نفسه طبقة التكامل.",
      approach:
        "مساحة عمل واحدة موثّقة يحمل فيها المشروع طابور الجودة وخطّ المحتوى وخطّ الإيراد معاً — فتصبح الحالة استعلاماً واحداً بدل أربع تنقّلات بين التبويبات.",
      outcome:
        "حيّ وقيد الاستخدام اليومي — هذا الاستوديو يخطّط ويتتبّع ويفوتر عمله من خلاله، بما في ذلك الموقع الذي تقرأه الآن.",
    },
  },
  juhaynh: {
    sq: {
      lede:
        "Një shtëpi blerjesh, jo një dyqan: tetë vende, njëzet e gjashtë çanta qytetesh, secila një koleksion i mbyllur pjesësh të kuruara që i rrotullon në 3D para se t'i hapësh.",
      problem:
        "Tregtia e luksit online rrafshohet në të njëjtën rrjetë produktesh me filtra, pavarësisht se çfarë shitet. Prejardhja — arsyeja pse një pjesë meriton të blihet — shtypet në një përshkrim që s'e lexon njeri.",
      approach:
        "Katalogu ristrukturohet si kapituj: një vend hapet në qytetet e tij, një qytet hap një çantë, dhe çanta i paraqet pjesët si objekte që i tërheq për t'i rrotulluar dhe i prek për t'i hapur. Arabisht-së-pari me regjistër anglez, çmime në SAR dhe përputhshmëri tregtare saudite (verifikim Maroof, ankesa Balagh) të integruara.",
      outcome:
        "Live në tetë vende dhe njëzet e gjashtë çanta qytetesh, nga Riadi dhe AlUla deri në Tokio, Londër e Paris.",
    },
    ar: {
      lede:
        "دارُ اقتناء لا متجر: ثماني دول، وستّ وعشرون حقيبة مدينة، كلٌّ منها مجموعة مختومة من القطع المنتقاة تُديرها بالأبعاد الثلاثة قبل فتحها.",
      problem:
        "تجارة الفخامة على الإنترنت تنهار إلى شبكة المنتجات المفلترة نفسها مهما كان المعروض. أمّا الأصل والحكاية — سبب استحقاق القطعة للاقتناء — فيُسطّح إلى وصف لا يقرأه أحد.",
      approach:
        "أُعيدت هيكلة الكتالوج فصولاً: الدولة تنفتح على مدنها، والمدينة تفتح حقيبة، والحقيبة تعرض قطعها أجساماً تسحبها لتدور حولها وتنقرها لتفتحها. عربيةٌ أولاً بسجلّ إنجليزي، وتسعير بالريال السعودي، وامتثال تجاري سعودي (توثيق معروف، وبلاغ للشكاوى) مدمج.",
      outcome:
        "حيّ في ثماني دول وستّ وعشرين حقيبة مدينة، من الرياض والعُلا إلى طوكيو ولندن وباريس.",
    },
  },
  "umrah-platform": {
    sq: {
      lede:
        "Paketa premium umreje me hotele pesëyjeshe, ture të udhëhequra dhe asistencë të plotë vize — të rezervueshme në shqip, anglisht ose arabisht.",
      problem:
        "Pelegrinët shqipfolës e rezervonin umrenë përmes ndërmjetësish që nuk publikonin asgjë: as çmime, as itinerare, as procesin e vizës, as ndonjë mënyrë për të krahasuar një agjenci me tjetrën.",
      approach:
        "Një sajt plotësisht trigjuhësh — çdo rrugë e pasqyruar në sq/en/ar — me paketa, çmime transparente, blog dhe FAQ, me rezervim me WhatsApp ose online dhe vetë-regjistrim për agjencitë partnere që duan të listohen.",
      outcome:
        "Live në të tre gjuhët me regjistrimin e agjencive të hapur; katalogu i paketave po mbushet përpara sezonit të ardhshëm të pelegrinazhit.",
    },
    ar: {
      lede:
        "باقات عمرة فاخرة بفنادق خمس نجوم وجولات مصحوبة بمرشدين ومساعدة كاملة في التأشيرة — تُحجز بالألبانية أو الإنجليزية أو العربية.",
      problem:
        "كان المعتمرون الناطقون بالألبانية يحجزون العمرة عبر وسطاء لا ينشرون شيئاً: لا أسعار، ولا برامج، ولا مسار تأشيرة، ولا سبيلَ لمقارنة وكالة بأخرى.",
      approach:
        "موقع ثلاثي اللغة بالكامل — كل مسار له مرآة في sq/en/ar — يحمل الباقات وتسعيراً شفافاً ومدونة وأسئلة شائعة، مع حجز عبر واتساب أو إلكترونياً وتسجيل ذاتي للوكالات الشريكة الراغبة بالإدراج.",
      outcome:
        "حيّ باللغات الثلاث وتسجيل الوكالات مفتوح؛ ويجري ملء كتالوج الباقات قبل موسم الاعتمار المقبل.",
    },
  },
  "discover-albania": {
    sq: {
      lede:
        "Njëqind e dyzet e nëntë destinacione shqiptare në gjashtë tema, me hartë, kërkim dhe një ndërtues që e kthen cilindo prej tyre në një udhërrëfyes shumëditor të personalizuar.",
      problem:
        "Përmbajtja turistike e Shqipërisë ishte e shpërndarë nëpër blogje dhe broshura PDF. Asgjë nuk e çonte vizitorin nga shfletimi i një krahine te një plan i vërtetë ditë-për-ditë në dorë.",
      approach:
        "Një bazë e strukturuar destinacionesh — det, dimër, gastronomi, histori, natyrë, qytet — me hartë dhe kërkim mbi gjithë grupin, udhërrëfyes shumëditorë të kuruar dhe një gjenerator që monton udhërrëfyes të personalizuar nga destinacionet e zgjedhura. Trigjuhësh EN/SQ/AR kudo.",
      outcome:
        "Live me 149 destinacione, gjashtë kategori tematike dhe udhërrëfyes të kuruar e të personalizuar.",
    },
    ar: {
      lede:
        "مئة وتسع وأربعون وجهة ألبانية ضمن ستة محاور، مع خريطة وبحث ومنشئ يحوّل أيّاً منها إلى دليل متعدد الأيام مخصّص.",
      problem:
        "كان المحتوى السياحي لألبانيا مبعثراً بين تدوينات وكتيّبات PDF. لا شيء ينقل الزائر من تصفّح منطقة إلى خطة فعلية يوماً بيوم في يده.",
      approach:
        "قاعدة وجهات منظّمة — بحر، شتاء, مطبخ، تاريخ، طبيعة، مدينة — بخريطة وبحث فوق المجموعة كلّها، وأدلة متعددة الأيام منتقاة، ومولّد يركّب دليلاً مخصّصاً من الوجهات المختارة. ثلاثي اللغة EN/SQ/AR في كل موضع.",
      outcome:
        "حيّ بـ 149 وجهة وست فئات موضوعية وأدلة منتقاة ومخصّصة معاً.",
    },
  },
  "ftesa-ime": {
    sq: {
      lede:
        "Ftesa dixhitale shqip-së-pari që mbërrijnë si përvojë: 3D interaktiv që mysafirët e rrotullojnë, të dërguara me WhatsApp, me RSVP që vijnë në kohë reale.",
      problem:
        "Platformat ndërkombëtare të ftesave nuk flasin shqip dhe nuk i përshtaten mënyrës si zhvillohet realisht një dasmë shqiptare. Gjithçka tjetër do të thoshte një imazh statik i hedhur në një grup bisede — dhe përgjigjet të numëroheshin me dorë.",
      approach:
        "Një atelie shabllonesh ku çdo dizajn mbart tipografinë dhe lëvizjen e vet, 3D interaktiv që mysafirët e rrotullojnë dhe eksplorojnë, dërgim me një lidhje të vetme përmes WhatsApp, SMS ose email pa asnjë aplikacion për t'u instaluar, eksport video për Stories dhe TikTok, dhe gjurmim RSVP-sh për mikpritësin.",
      outcome:
        "Live në shqip dhe anglisht me katër shabllone të punuar me dorë, kalendar festash dhe gjurmim RSVP-sh në punë për evente reale.",
    },
    ar: {
      lede:
        "دعوات رقمية ألبانيةٌ أولاً تصل كتجربة: أبعاد ثلاثية تفاعلية يديرها ضيوفك، تُرسل عبر واتساب، مع تأكيدات حضور تصل لحظياً.",
      problem:
        "منصات الدعوات العالمية لا تتكلم الألبانية ولا تناسب الطريقة التي يُدار بها العرس الألباني فعلاً. وكل ما عداها كان يعني صورة جامدة تُرمى في مجموعة دردشة — وعدّ الردود يدوياً.",
      approach:
        "محترف قوالب يحمل كل تصميم فيه خطّه وحركته، وأبعاد ثلاثية تفاعلية يديرها الضيوف ويستكشفونها، وتسليم برابط واحد عبر واتساب أو SMS أو البريد دون أي تطبيق يُثبّت، وتصدير فيديو للقصص وتيك توك، وتتبّع تأكيدات الحضور للمضيف.",
      outcome:
        "حيّ بالألبانية والإنجليزية بأربعة قوالب مصنوعة بعناية، وتقويم مناسبات، وتتبّع حضور يعمل لفعاليات حقيقية.",
    },
  },
  "creed-vr": {
    sq: {
      lede:
        "Shtatë përvoja VR të historisë islame në Xhaminë e Namazgjasë në Tiranë, me seanca që rezervohen drejtpërdrejt nga sajti.",
      problem:
        "Historia fetare dhe kulturore përcillet me vështirësi përmes tekstit dhe imazheve statike — dhe një qendër fizike VR ka gjithsesi nevojë që rezervimet, orarët e seancave dhe udhëzimet të zgjidhen para se dikush të hyjë brenda.",
      approach:
        "Një sajt rezervimesh dygjuhësh që e shet përvojën para vizitës — paraqitje me lëvizje e shtatë përvojave, detajet e qendrës dhe rrugët e kontaktit — me rezervimin si thirrja e vetme për veprim kudo.",
      outcome:
        "Live dhe në funksionim si qendër në Tiranë, duke pranuar rezervime online dhe me telefon.",
    },
    ar: {
      lede:
        "سبع تجارب واقع افتراضي في التاريخ الإسلامي في جامع النمازغاه بتيرانا، تُحجز جلساتها مباشرة من الموقع.",
      problem:
        "التاريخ الديني والثقافي يصعب نقله بالنص والصور الجامدة — ومقرّ الواقع الافتراضي المادي يحتاج مع ذلك إلى حلّ للحجوزات وجدولة الجلسات والإرشادات قبل أن يدخل أحد.",
      approach:
        "موقع حجز ثنائي اللغة يبيع التجربة قبل الزيارة — عرض حركي للتجارب السبع، وتفاصيل المقرّ وقنوات التواصل — والحجز هو نداء الفعل الوحيد في كل موضع.",
      outcome:
        "حيّ ويعمل مقرّاً في تيرانا، يستقبل الحجوزات إلكترونياً وعبر الهاتف.",
    },
  },
  termka: {
    sq: {
      lede:
        "Një vitrinë me një produkt të vetëm për mbajtësen e telefonit Apis prej arre masive, e shitur përmes historisë së punimit — jo përmes një tabele specifikash.",
      problem:
        "Një markë me një produkt të vetëm nuk mbështetet dot te gjerësia e katalogut. Nëse vetë objekti nuk bëhet bindës, në sajt nuk mbetet asgjë tjetër për të konvertuar.",
      approach:
        "Faqja është ndërtuar si argument: problemi i telefonit të shtrirë rrafsh, objekti i prezantuar, pastaj punishtja — druri i përzgjedhur, i latuar, i përfunduar me vaj-dyllë — para se të vijnë specifikat. Arkë me Stripe dhe i instalueshëm si PWA.",
      outcome:
        "Live dhe në shitje: mbajtësja Apis me £49.99, me transport falas në Mbretërinë e Bashkuar.",
    },
    ar: {
      lede:
        "واجهة متجر بمنتج واحد لحامل الهاتف Apis من خشب الجوز الصلب، يُباع عبر حكاية صنعته لا عبر جدول مواصفات.",
      problem:
        "علامة المنتج الواحد لا تتكئ على اتساع كتالوج. إن لم يُجعل الشيء نفسه مقنعاً، فليس في الموقع شيء آخر يحوّل الزائر إلى مشترٍ.",
      approach:
        "بُنيت الصفحة حجّةً متسلسلة: مشكلة الهاتف المستلقي منبسطاً، ثم تقديم القطعة، ثم الورشة — خشب يُنتقى ويُشكَّل ويُنهى بشمع الزيت — قبل أن تصل المواصفات. دفع عبر Stripe، وقابل للتثبيت كتطبيق PWA.",
      outcome:
        "حيّ ويبيع حامل Apis بسعر 49.99 جنيهاً إسترلينياً مع شحن مجاني داخل المملكة المتحدة.",
    },
  },
  // Replaces the retired zerolanguage entry — the Academy is that product's
  // evolution, and /case/zerolanguage now redirects to /case/academy.
  academy: {
    sq: {
      lede:
        "Një platformë kursesh ku përparimi fitohet: një njeri i vërtetë lexon atë që dorëzoni, dhe mësimi tjetër hapet vetëm kur puna juaj miratohet.",
      problem:
        "Platformat e kurseve me vetë-ritëm e lënë këdo të klikojë 'tjetra' derisa të shfaqet një certifikatë. Përfundimi pushon së thëni gjë, dhe nxënësit që vërtet donin koment marrin një vlerësues automatik ose heshtje.",
      approach:
        "Kurset hapen mësim pas mësimi. Ju e punoni materialin dhe dorëzoni me fjalët tuaja; rishikuesi i kursit e lexon dhe përgjigjet me shkrim nën një kod reference, dhe miratimi hap atë që vjen më pas. Vetë regjistrimi është një aplikim që lexohet nga një njeri, mësuesit mund të hapin mësime përpara kur kjo i shërben nxënësit, dhe kurset nisin si drafte private derisa mësuesi t'i publikojë.",
      outcome:
        "Në beta të mbyllur në academy.amalbryx.com — shqip së pari, i instalueshëm si PWA, me dorëzime reale që marrin shqyrtim njerëzor me shkrim.",
    },
    ar: {
      lede:
        "منصة دورات يُكتسب فيها التقدّم: إنسان حقيقي يقرأ ما تسلّمه، ولا يُفتح الدرس التالي إلا حين يُعتمد عملك.",
      problem:
        "منصات الدورات ذاتية الوتيرة تدع أي أحد ينقر «التالي» حتى تظهر شهادة. يفقد الإتمام معناه، ومن أراد ملاحظات فعلية يحصل على مصحّح آلي أو صمت.",
      approach:
        "تُفتح الدورات درساً بعد درس. تعمل على المادة وتسلّم بكلماتك؛ يقرأ مراجعُ الدورة ما سلّمته ويرد كتابةً تحت كود مرجعي، والاعتماد يفتح ما يليه. الالتحاق نفسه طلبٌ يقرؤه إنسان، ويمكن للمعلمين فتح دروس مسبقاً حين يخدم ذلك المتعلّم، وتبدأ الدورات مسودات خاصة إلى أن ينشرها معلّمها.",
      outcome:
        "في نسخة تجريبية مغلقة على academy.amalbryx.com — ألبانية أولاً، قابلة للتثبيت كتطبيق PWA، وتسليمات حقيقية تتلقى مراجعة بشرية مكتوبة.",
    },
  },
};

/** SQ/AR deep copy for a slug, or undefined to fall back to EN canonical. */
export function localizedCaseDeepCopy(
  slug: string,
  locale: Locale,
): CaseDeepCopy | undefined {
  if (locale === "en") return undefined;
  return CASE_TRANSLATIONS[slug]?.[locale];
}
